import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  USER_ACTIVITY_GROUP_LABELS,
  USER_ACTIVITY_INFO,
  userActivityGroup,
  type SortDirection,
  type UserActivityAction,
  type UserActivityActionCount,
} from '@ai-meeting/shared'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchAdminActivitySummary } from '@/services/admin'
import ActivityFailuresDialog from './ActivityFailuresDialog'
import ActivityPeopleDialog from './ActivityPeopleDialog'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminDateRange from './AdminDateRange'
import { dateInputToEndMs, dateInputToStartMs } from './adminFormat'
import { sortActivityRows, type ActivitySortBy } from './activitySort'

interface ActivityTabProps {
  enabled: boolean
  /** Date range, owned by the page — see `MeetingsTab`. */
  dateFrom: string
  dateTo: string
  onDateFromChange: (value: string) => void
  onDateToChange: (value: string) => void
}

/**
 * How often each function was used (BRAI-677).
 *
 * Does not use `useAdminTable`, which every other tab here does: that hook is
 * built for a paged, searched, server-sorted endpoint, and this answer is one
 * row per action in a fixed list. It arrives whole and is ordered in the
 * browser.
 *
 * Every action appears, including the ones nobody has touched. A zero is the
 * most interesting row in a report like this, and showing only what has rows
 * would hide exactly the feature worth asking about.
 */
export default function ActivityTab({
  enabled,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: ActivityTabProps) {
  const { t } = useUiStrings()
  const [rows, setRows] = useState<UserActivityActionCount[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<ActivitySortBy>('total')
  const [sortDir, setSortDir] = useState<SortDirection>('desc')
  const [openDetail, setOpenDetail] = useState<{
    action: UserActivityAction
    kind: 'people' | 'failures'
  } | null>(null)

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    setLoading(true)
    setError(null)
    void fetchAdminActivitySummary({
      dateFrom: dateInputToStartMs(dateFrom),
      dateTo: dateInputToEndMs(dateTo),
    })
      .then((response) => {
        if (!cancelled) setRows(response.data)
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, dateFrom, dateTo])

  const toggleSort = useCallback((column: ActivitySortBy) => {
    setSortBy((current) => {
      // Same column flips the direction; a different one starts from the way
      // round that reads first — names from A, numbers from the biggest.
      if (current === column) {
        setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'))
        return current
      }
      setSortDir(column === 'action' || column === 'group' ? 'asc' : 'desc')
      return column
    })
  }, [])

  const sorted = useMemo(() => sortActivityRows(rows, sortBy, sortDir), [rows, sortBy, sortDir])
  const openRow = openDetail ? (rows.find((row) => row.action === openDetail.action) ?? null) : null
  const closeDetail = useCallback(() => setOpenDetail(null), [])

  const columns: AdminColumn<UserActivityActionCount, ActivitySortBy>[] = [
    {
      key: 'group',
      label: t.adminColArea,
      sortBy: 'group',
      render: (row) => (
        <span className="text-[var(--text-secondary)]">
          {USER_ACTIVITY_GROUP_LABELS[userActivityGroup(row.action)]}
        </span>
      ),
    },
    {
      key: 'action',
      label: t.adminColFunction,
      sortBy: 'action',
      // Wrapping, unlike every other cell in this table: the description is a
      // sentence, and on the `whitespace-nowrap` the table defaults to it
      // would push the counts off the right of the screen. The cap goes on the
      // inner element rather than the cell because a max-width on a `<td>` is
      // advisory under `table-layout: auto`.
      //
      // Measured rather than assumed: with the cap on the inner div it holds
      // at 416px from a 1400px content column down to 600px, and the table
      // stops growing at 867px — below that it scrolls inside its own box, as
      // the ten-column Users tab already does, instead of moving the page.
      cellClassName: 'whitespace-normal align-top',
      render: (row) => (
        <div className="max-w-[26rem]">
          <span className="font-medium">{USER_ACTIVITY_INFO[row.action].label}</span>
          {/* The wire value, kept visible rather than hidden in a tooltip: it
              is what the stored row holds and what anyone reading the table by
              hand has to match on. */}
          <span className="ml-2 font-mono text-[10px] text-[var(--text-tertiary)]">
            {row.action}
          </span>
          <p className="mt-0.5 text-xs leading-5 text-[var(--text-secondary)]">
            {USER_ACTIVITY_INFO[row.action].description}
          </p>
        </div>
      ),
    },
    {
      key: 'total',
      label: t.adminColUses,
      sortBy: 'total',
      render: (row) => <span className="tabular-nums">{row.total}</span>,
    },
    {
      key: 'succeeded',
      label: t.adminColSucceeded,
      sortBy: 'succeeded',
      render: (row) => <span className="tabular-nums">{row.succeeded}</span>,
    },
    {
      key: 'failed',
      label: t.adminColFailed,
      sortBy: 'failed',
      // Red only when there is something to be red about: a column of zeros
      // picked out in red reads as a screen full of problems.
      render: (row) =>
        row.failed > 0 ? (
          <button
            type="button"
            onClick={() => setOpenDetail({ action: row.action, kind: 'failures' })}
            aria-label={fmt(t.adminActivityFailuresButton, {
              count: row.failed,
              label: USER_ACTIVITY_INFO[row.action].label,
            })}
            className="font-semibold tabular-nums text-red-600 underline-offset-2 hover:underline"
          >
            {row.failed}
          </button>
        ) : (
          <span className="tabular-nums">0</span>
        ),
    },
    {
      key: 'users',
      label: t.adminColDistinctUsers,
      sortBy: 'users',
      render: (row) =>
        row.users > 0 ? (
          <button
            type="button"
            onClick={() => setOpenDetail({ action: row.action, kind: 'people' })}
            aria-label={fmt(t.adminActivityPeopleButton, {
              count: row.users,
              label: USER_ACTIVITY_INFO[row.action].label,
            })}
            className="font-semibold tabular-nums text-[var(--accent)] underline-offset-2 hover:underline"
          >
            {row.users}
          </button>
        ) : (
          <span className="tabular-nums">0</span>
        ),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <AdminDateRange
          from={dateFrom}
          to={dateTo}
          onFromChange={onDateFromChange}
          onToChange={onDateToChange}
        />
      </div>
      <AdminDataTable
        columns={columns}
        rows={sorted}
        rowKey={(row) => row.action}
        loading={loading}
        error={error}
        sortBy={sortBy}
        sortDir={sortDir}
        onToggleSort={toggleSort}
        // One page, always: the list is as long as the list of functions.
        page={1}
        totalPages={1}
        total={sorted.length}
        onPageChange={() => undefined}
      />
      {openRow && openDetail?.kind === 'people' && openRow.people.length > 0 ? (
        <ActivityPeopleDialog row={openRow} onClose={closeDetail} />
      ) : null}
      {openRow && openDetail?.kind === 'failures' && openRow.failures.length > 0 ? (
        <ActivityFailuresDialog row={openRow} onClose={closeDetail} />
      ) : null}
    </div>
  )
}
